import { css } from 'aphrodite/no-important';
import Bowser from 'bowser';
// components
import { AgendaWidget, Speaker, SpeakerType } from 'components';
import { Branding, BrandingType } from 'components/Nav/Branding/Branding';
import { useScrollToTopOnMount } from 'hooks';
// models
import { IGenericObject, LoginData } from 'models';
import React, { ChangeEvent, FC, FormEvent, useEffect, useState } from 'react';
// utils
import api, { VoteApiResponse, VOTE_API_ACTION_TYPE, VOTE_API_RESPONSE_CODE } from 'util/api';
import { GAEventAction, GAEventCategory, trackGoogleEvent } from 'util/google-helpers';
import { checkIfTrue, decodeObject } from 'util/helpers';
// styles
import { speakerStyle, style } from './style';
import './style.scss';

const CODE_MAX_LENGTH = 6;
interface Props {
  cmsData: IGenericObject;
  onLogin: (loginData: LoginData) => any;
  settings: IGenericObject;
}

export const Home: FC<Props> = ({ cmsData, settings, onLogin }) => {
  useScrollToTopOnMount();

  const { welcome, featured_speakers, header } = cmsData.landing;

  const styles = style();

  const [code, setCode] = useState('');
  const [validApiResponse, setValidApiResponse] = useState(true);
  const [submitEnabled, setSubmitEnabled] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  /* Form validation */
  useEffect(() => {
    const codeFullLength = code.length === CODE_MAX_LENGTH;

    setSubmitEnabled(codeFullLength && validApiResponse);
  }, [code, validApiResponse]);

  const scrollToAgenda = () => {
    const agendaSectionRef = document.querySelector(`.${css(styles.agendaSection)}`);
    if (!agendaSectionRef) return;

    // need to use type assertion here
    window.scrollTo({
      top: (agendaSectionRef as HTMLElement).offsetTop,
      behavior: 'smooth',
    });
  };
  /* Returns a an array of featured speakers */
  const renderSpeakers = () => {
    const speakers = featured_speakers.speakers.filter((speaker: any) => {
      return !!speaker.image_url && !!speaker.title_text && !!speaker.subtitle_text;
    });
    const columns = speakers.length === 3 ? 3 : 2;
    const speakerStyles = speakerStyle(columns);
    return (
      <div className={css(speakerStyles.container)}>
        {speakers.map(
          (
            {
              image_url,
              title_text,
              subtitle_text,
            }: {
              image_url: string;
              title_text: string;
              subtitle_text: string;
            },
            index: number
          ) => {
            let type = SpeakerType.DEFAULT;
            return (
              <Speaker
                key={index}
                imageUrl={image_url}
                title={title_text}
                subtitle={subtitle_text}
                type={type}
                columns={columns}
              />
            );
          }
        )}
      </div>
    );
  };

  /* Returns the welcome section */
  const renderWelcomeSection = () => {
    const showRegistrationLink = checkIfTrue(cmsData.landing.settings.display_registration_link);

    return (
      <section className={css(styles.welcomeSection)}>
        <h2 className={css(styles.h2)}>{welcome.header_text}</h2>
        <div dangerouslySetInnerHTML={{ __html: welcome.description_text }}></div>
        <form className={css(styles.loginForm)} onSubmit={handleFormSubmit}>
          {welcome.login_button_placeholder_text && (
            <label className={css(styles.inputLabel)} htmlFor='login'>
              {welcome.login_button_placeholder_text}
            </label>
          )}

          <input
            id='login'
            className={css(styles.loginInput)}
            type='text'
            placeholder={welcome.login_label_text}
            maxLength={CODE_MAX_LENGTH}
            onChange={handleCodeChange}
          />
          <p className={css(styles.errorMessage)}>{errorMessage}</p>
          <button disabled={!submitEnabled} type='submit' className={css(styles.loginButton)}>
            {welcome.login_button_text}
          </button>
          {showRegistrationLink && welcome.registration_link_url && welcome.registration_link_text && (
            <a
              href={welcome.registration_link_url}
              target='_blank'
              rel='noopener noreferrer'
              onClick={() =>
                trackGoogleEvent(GAEventCategory.SITE, GAEventAction.CLICK, welcome.registration_link_text)
              }>
              {welcome.registration_link_text}
            </a>
          )}
        </form>
      </section>
    );
  };

  /* Called when the user attempts to login using their access code
   * Call the voteAPI to check if the code is valid. If so, set the user data returned in the response
   * If not valid, display the appropriate error message
   */
  const handleFormSubmit = async (event: FormEvent<HTMLFormElement>): Promise<any> => {
    const { apiKey, version_id } = settings;

    event.preventDefault();

    const browser = Bowser.getParser(window.navigator.userAgent);
    const browserName = browser.getBrowserName();
    const deviceType = browser.getPlatformType();

    const payload = {
      apiKey,
      timestamp: Date.now(),
      code: code.toUpperCase(),
      action_type: VOTE_API_ACTION_TYPE.LOGIN,
      device: deviceType,
      browser: browserName,
    };

    try {
      const response: VoteApiResponse = await api.postToVoteApi(payload, null, version_id);

      // Successful Login
      if (response.response_code === VOTE_API_RESPONSE_CODE.VALID) {
        //DynamoDB and the ruleset return URI encoded values, so need to decode them.
        const personalData = decodeObject(JSON.parse(response.personal_data));
        const sessionData = decodeObject(JSON.parse(response.session));

        const loginData = {
          userData: {
            code,
            ...personalData,
            ...sessionData,
          },
          isSurveyCompleted: response.survey_completed === 'true',
        } as LoginData;

        onLogin(loginData);
      } else {
        // Failed Login
        const loginErrors = welcome.login_errors;

        switch (response.response_code) {
          case VOTE_API_RESPONSE_CODE.OVERLIMIT:
            setErrorMessage(loginErrors.overlimit);
            break;
          case VOTE_API_RESPONSE_CODE.OUT_OF_WINDOW:
            setErrorMessage(loginErrors.out_of_window);
            break;
          case VOTE_API_RESPONSE_CODE.DUPLICATE_LOGIN:
            setErrorMessage(loginErrors.duplicate_login);
            break;
          case VOTE_API_RESPONSE_CODE.OUT_OF_GEO:
            setErrorMessage(loginErrors.out_of_geo);
            break;
          default:
          case VOTE_API_RESPONSE_CODE.GENERAL_INVALID:
            setErrorMessage(loginErrors.general_error);
            break;
        }
      }
    } catch (e) {
      console.log('Error Occurred Trying to Login', e);
    }
  };

  //Update code state, and reset Api Response state
  const handleCodeChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setCode(e.target.value.toUpperCase());
    setValidApiResponse(true);
    setErrorMessage('');
  };

  return (
    <main className={css(styles.mainContainer)}>
      <div className={css(styles.welcomeSectionContainer)}>
        <Branding type={BrandingType.VERTICAL} logoUrl={header.logo_url} copy={header.header_text} isVisible={true} />
        {renderWelcomeSection()}
        <div className={css(styles.scrollDown)}>
          <button
            onClick={() => {
              scrollToAgenda();
            }}>
            <img src='/images/scroll-down.png' alt='scroll down' />
          </button>
          <p>Scroll down for more information</p>
        </div>
      </div>

      <section className={css(styles.agendaSection)}>
        <AgendaWidget agendaData={cmsData.landing.agenda} basic />
      </section>
      <section className={css(styles.featuredSpeakersSection)}>
        <h2 className={css(styles.h2, styles.agendaHeadline)}>{featured_speakers.title_text}</h2>
        <div className={css(styles.speakersGrid)}>{renderSpeakers()}</div>
      </section>
    </main>
  );
};
